var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { computed, readonly, ref } from "vue";
import { errorMessageAsString } from "@/utils/simple-error";
const DEFAULT_POLL_DELAY = 10000;
/**
 * Composable for waiting (polling) on generic background tasks or processes.
 */
export function useGenericMonitor(options) {
    var _a;
    let timeout = null;
    let pollDelay = (_a = options.defaultPollDelay) !== null && _a !== void 0 ? _a : DEFAULT_POLL_DELAY;
    const isRunning = ref(false);
    const taskStatus = ref();
    const requestId = ref();
    const requestHasFailed = ref(false);
    const isCompleted = computed(() => options.completedCondition(taskStatus.value));
    const hasFailed = computed(() => options.failedCondition(taskStatus.value));
    function isFinalState(status) {
        return options.completedCondition(status) || options.failedCondition(status);
    }
    function loadStatus(storedStatus) {
        taskStatus.value = storedStatus;
    }
    function waitForTask(taskId, pollDelayInMs) {
        return __awaiter(this, void 0, void 0, function* () {
            pollDelay = pollDelayInMs !== null && pollDelayInMs !== void 0 ? pollDelayInMs : pollDelay;
            resetState();
            requestId.value = taskId;
            isRunning.value = true;
            return fetchTaskStatus(taskId);
        });
    }
    function fetchTaskStatus(taskId) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const result = yield options.fetchStatus(taskId);
                taskStatus.value = result;
                if (isCompleted.value || hasFailed.value) {
                    isRunning.value = false;
                }
                else {
                    pollAfterDelay(taskId);
                }
            }
            catch (err) {
                handleError(errorMessageAsString(err));
            }
        });
    }
    function pollAfterDelay(id) {
        resetTimeout();
        timeout = setTimeout(() => {
            fetchTaskStatus(id);
        }, pollDelay);
    }
    function handleError(err) {
        taskStatus.value = err.toString();
        requestHasFailed.value = true;
        isRunning.value = false;
        resetTimeout();
    }
    function resetTimeout() {
        if (timeout) {
            clearTimeout(timeout);
            timeout = null;
        }
    }
    function resetState() {
        resetTimeout();
        taskStatus.value = undefined;
        requestHasFailed.value = false;
        isRunning.value = false;
    }
    return {
        waitForTask,
        isFinalState,
        loadStatus,
        isRunning: readonly(isRunning),
        isCompleted: readonly(isCompleted),
        hasFailed: readonly(hasFailed),
        requestHasFailed: readonly(requestHasFailed),
        taskStatus: readonly(taskStatus),
        expirationTime: options.expirationTime,
    };
}
